import React from 'react';
import { textColorOn, mutedTextColorOn } from '../../utils/themeContrast';
import { ThankYouTheme } from './ThankYouPreview';

/**
 * The survey welcome screen, replicated for the wizard.
 *
 * Mirrors brand-survey-app's Survey.tsx welcome branch and its WelcomePage
 * component: a 448px column holding a centred header, the progress bar with its
 * step and percentage labels, then the card carrying the Begin button and the
 * data-quality disclosure.
 *
 * The previous hand-built preview had no card at all, a 32px title against the
 * real 24px, and a progress bar pinned to the bottom, so it agreed with the
 * live page on almost nothing. If Survey.tsx or WelcomePage.tsx change, this
 * has to move with them — nothing enforces it, they are different repos.
 */
export const WelcomePreview: React.FC<{
  title: string;
  subtitle?: string | null;
  theme?: ThankYouTheme | null;
  showProgressBar?: boolean;
  // Total steps the real page would count. Only affects the label text.
  totalSteps?: number;
}> = ({ title, subtitle, theme, showProgressBar = true, totalSteps = 13 }) => {
  // Same fallbacks the survey app uses.
  const background = theme?.backgroundColor || '#312e81';
  const card = theme?.cardColor || '#3730a3';
  const primary = theme?.primaryColor || '#4338ca';
  const secondary = theme?.secondaryColor || '#2563eb';

  const pageText = textColorOn(background);
  const pageMuted = mutedTextColorOn(background);
  const cardMuted = mutedTextColorOn(card);
  const secondaryText = textColorOn(secondary);

  // The real page shows step 1 of N here.
  const progress = totalSteps > 0 ? (1 / totalSteps) * 100 : 0;

  return (
    <div
      style={{
        background,
        borderRadius: 12,
        padding: 24,
        display: 'flex',
        justifyContent: 'center',
        overflowX: 'auto',
      }}
    >
      <div style={{ width: 448, flex: '0 0 448px', boxSizing: 'border-box' }}>
        {/* Header */}
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 8,
              color: pageText,
              lineHeight: 1.25,
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div style={{ color: pageMuted, fontSize: 16, lineHeight: 1.5 }}>
              {subtitle}
            </div>
          )}
        </div>

        {showProgressBar && (
          <div style={{ marginBottom: 32 }}>
            <div
              style={{
                height: 4,
                width: '100%',
                borderRadius: 9999,
                overflow: 'hidden',
                backgroundColor: `${primary}80`,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progress}%`,
                  backgroundColor: secondary,
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 8,
                fontSize: 12,
                color: pageMuted,
              }}
            >
              <span>Step 1 of {totalSteps} - Welcome</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
          </div>
        )}

        {/* The card itself */}
        <div
          style={{
            background: card,
            border: `1px solid ${primary}`,
            borderRadius: 24,
            padding: 24,
            boxSizing: 'border-box',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <span
              style={{
                background: secondary,
                color: secondaryText,
                padding: '12px 32px',
                borderRadius: 12,
                fontWeight: 500,
                fontSize: 16,
              }}
            >
              Begin Survey
            </span>
          </div>
          {/* Response Integrity (SYR-48) disclosure, shown on the real page. */}
          <p
            style={{
              marginTop: 16,
              marginBottom: 0,
              textAlign: 'center',
              fontSize: 12,
              color: cardMuted,
            }}
          >
            We collect device characteristics to ensure data quality.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomePreview;
